import logger from './logger';

/**
 * Extracts node objects from GraphQL Relay connection edges, filtering out null values.
 * @param {object} root0 - The destructured parameter object
 * @param {Array<object>} root0.edges - Array of edge objects from a GraphQL Relay connection.
 *   Each edge should have a `node` property containing the actual data object.
 * @returns {Array<object>} Array of non-null node objects extracted from the edges.
 */
export const getNodesFromEdges = ({ edges }) => edges.map((el) => (el.node)).filter((el) => !!el);

/**
 * Generic retry wrapper for GraphQL queries with timeout handling.
 * Properly cancels Relay subscriptions on timeout to prevent resource leaks and race conditions.
 * @param {object} options - Configuration options
 * @param {Function} options.queryFn - Function that returns a Promise with an optional
 * unsubscribe method
 * @param {number} options.timeoutMs - Timeout in milliseconds
 * @param {string} options.operationName - Name for logging purposes
 * @param {number} [options.maxRetries=3] - Maximum retry attempts
 * @returns {Promise<any>} Result of the query or fallback value
 */
export const queryWithRetry = async ({
  queryFn,
  timeoutMs,
  operationName,
  maxRetries = 3,
}) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await new Promise((resolve, reject) => {
        let promiseWithSubscription = null;
        let isSettled = false;

        const timeoutId = setTimeout(() => {
          if (!isSettled) {
            isSettled = true;
            logger.warn(`${operationName} timed out (attempt ${attempt}/${maxRetries})`);

            // Cancel the subscription to prevent resource leaks and race conditions
            if (promiseWithSubscription && typeof promiseWithSubscription.unsubscribe === 'function') {
              logger.log(`Cancelling subscription for ${operationName} due to timeout`);
              promiseWithSubscription.unsubscribe();
            }

            reject(new Error('Request timed out'));
          }
        }, timeoutMs);

        try {
          promiseWithSubscription = queryFn();

          // Handle the case where queryFn returns a Promise (with or without unsubscribe method)
          if (promiseWithSubscription && typeof promiseWithSubscription.then === 'function') {
            promiseWithSubscription
              .then((data) => {
                if (!isSettled) {
                  isSettled = true;
                  clearTimeout(timeoutId);
                  resolve(data);
                }
              })
              .catch((error) => {
                if (!isSettled) {
                  isSettled = true;
                  clearTimeout(timeoutId);
                  // Log intermediate failures but don't trigger user-facing notifications
                  logger.error(`${operationName} failed (attempt ${attempt}/${maxRetries})`, error);
                  reject(error);
                }
              });
          } else if (!isSettled) {
            // If queryFn doesn't return a promise, reject immediately
            isSettled = true;
            clearTimeout(timeoutId);
            reject(new Error('queryFn must return a Promise'));
          }
        } catch (error) {
          if (!isSettled) {
            isSettled = true;
            clearTimeout(timeoutId);
            logger.error(`${operationName} failed to start (attempt ${attempt}/${maxRetries})`, error);
            reject(error);
          }
        }
      });

      return result;
    } catch (error) {
      lastError = error;
      // Log retry attempt but don't show user notification yet
      if (attempt < maxRetries) {
        logger.warn(`Retrying ${operationName} (attempt ${attempt + 1}/${maxRetries})`);
      }
    }
  }

  // All retry attempts exhausted - log final error and throw
  logger.error(`${operationName} failed after ${maxRetries} attempts`, lastError);
  throw lastError;
};
